// The toy audience (§13.5): loops and one-shots built from 10 detuned formant voices with a chorus
// spread, plus claps made of noise grains. Rendered offline like every other sound.
import type { Kit, Vowel } from '../sfx/kit';
import { FORMANTS } from '../sfx/kit';
import type { Recipe } from '../sfx/recipes';

const VOICES = 10;

interface CrowdVoiceOpts {
  vowel: Vowel | ((i: number) => Vowel);
  /** Pitch keyframes as multiples of the voice's own base pitch: [t, ratio]. */
  contour: readonly (readonly [number, number])[];
  /** Envelope: attack, hold, release (s) and peak. */
  a: number;
  hold: number;
  r: number;
  peak: number;
  at?: number;
  /** Syllabic amplitude pulses (laughter, chatter): rate Hz and depth 0..1. */
  pulse?: [number, number];
  /** Breathiness 0..1 (noise through the formants). */
  breath?: number;
}

/** One crowd member: a saw (or breath) through two formant bands, with its own pitch and timing. */
function crowdVoice(k: Kit, i: number, o: CrowdVoiceOpts, out: AudioNode, breath: AudioNode | null = null): void {
  // Children and adults: 140–330 Hz, spread so the chorus beats.
  const base = k.range(140, 330) * (1 + (i - VOICES / 2) * 0.004);
  const at = (o.at ?? 0) + k.range(0, 0.12);
  const end = at + o.a + o.hold + o.r + 0.05;
  const src = k.osc('sawtooth', base * o.contour[0][1], at, end);
  for (const [t, ratio] of o.contour) src.frequency.linearRampToValueAtTime(base * ratio * k.range(0.97, 1.03), at + t * k.range(0.9, 1.1));
  k.lfo(k.range(4.5, 6.5), base * 0.012, src.frequency, 'sine', at);
  const vowel = typeof o.vowel === 'function' ? o.vowel(i) : o.vowel;
  const [f1, f2] = FORMANTS[vowel];
  const sum = k.gain(1);
  const b1 = k.bp(f1 * k.range(0.92, 1.12), 6);
  const b2 = k.bp(f2 * k.range(0.92, 1.12), 8);
  const g2 = k.gain(0.5);
  src.connect(b1).connect(sum);
  src.connect(b2).connect(g2).connect(sum);
  if (o.breath && breath) {
    const nb = k.bp(f1 * 1.3, 2);
    const ng = k.gain(o.breath * 0.6);
    breath.connect(nb).connect(ng).connect(sum);
  }
  let node: AudioNode = sum;
  if (o.pulse) {
    const am = k.gain(1 - o.pulse[1] / 2);
    k.lfo(o.pulse[0] * k.range(0.85, 1.15), o.pulse[1] / 2, am.gain, 'sine', at);
    sum.connect(am);
    node = am;
  }
  const env = k.env({ a: o.a, d: 0.05, s: 0.9, hold: o.hold, r: o.r, peak: o.peak * k.range(0.6, 1), at });
  node.connect(env).connect(out);
}

function crowd(k: Kit, o: CrowdVoiceOpts): void {
  const bus = k.gain(1 / Math.sqrt(VOICES));
  const breath = o.breath ? k.noise('white') : null;
  for (let i = 0; i < VOICES; i++) crowdVoice(k, i, o, bus, breath);
  k.toOut(bus, k.lp(4200, 0.6));
}

/**
 * Applause: `rate(x)` claps per second (x = 0..1 through the span) as noise grains. Each clap is
 * one stroke on one of three band-limited noise layers (palm, cupped, finger), drawn into that
 * layer's amplitude curve, so a whole ovation is 9 nodes.
 */
function claps(k: Kit, at: number, dur: number, rate: (x: number) => number, peak: number): void {
  const res = 2000; // curve points per second
  // Claps start inside `dur`; the curve runs on until the last one has decayed (no truncation).
  const tail = 0.08;
  const n = Math.max(2, Math.ceil((dur + tail) * res));
  const bands: readonly (readonly [number, number])[] = [[1100, 1.2], [1800, 1.6], [2700, 2]];
  const curves = bands.map(() => new Float32Array(n));
  let t = 0;
  while (t < dur) {
    const r = Math.max(1, rate(t / dur));
    const c = curves[Math.floor(k.rand() * bands.length) % bands.length];
    const g = k.range(0.4, 1) * peak;
    const decay = k.range(0.006, 0.014);
    const i0 = Math.floor(t * res);
    for (let i = i0; i < Math.min(n, i0 + Math.ceil(decay * 5 * res)); i++) {
      const dt = (i - t * res) / res;
      const e = dt < 0.0006 ? Math.max(0, dt) / 0.0006 : Math.exp(-(dt - 0.0006) / decay);
      c[i] = Math.max(c[i], g * e);
    }
    t += -Math.log(1 - k.rand() * 0.999) / r;
  }
  const bus = k.gain(1);
  bands.forEach(([f, q], b) => {
    const curve = curves[b];
    curve[0] = 0;
    curve[n - 1] = 0;
    const amp = k.gain(0);
    amp.gain.setValueCurveAtTime(curve, at, dur + tail);
    k.chain(k.noise('white', at, at + dur + tail + 0.01), k.bp(f, q), amp, bus);
  });
  k.toOut(bus, k.hp(500));
}

export const CROWD_SOUNDS = {
  /** 4 s chatter bed (looped; its gain follows excitement). */
  murmur: {
    dur: 4.9,
    render(k) {
      const bus = k.gain(1 / Math.sqrt(VOICES));
      const vowels: readonly Vowel[] = ['a', 'e', 'o', 'u', 'i'];
      const breath = k.noise('white');
      for (let i = 0; i < VOICES; i++) {
        crowdVoice(k, i, {
          vowel: vowels[i % vowels.length], contour: [[0, 1], [1.3, 1.06], [2.6, 0.95], [4, 1]],
          a: 0.3, hold: 4.3, r: 0.2, peak: 0.35, at: 0, pulse: [k.range(3, 6), 0.8], breath: 0.3,
        }, bus, breath);
      }
      k.toOut(bus, k.lp(1800, 0.6));
    },
  },
  ooh: {
    dur: 2.1,
    render(k) {
      crowd(k, { vowel: (i) => (i % 2 === 0 ? 'u' : 'o'), contour: [[0, 0.85], [1.5, 1.28], [2, 1.3]], a: 0.9, hold: 0.6, r: 0.45, peak: 0.6 });
    },
  },
  aah: {
    dur: 1.6,
    render(k) {
      crowd(k, { vowel: 'a', contour: [[0, 1.35], [0.25, 1.4], [1.4, 1.0]], a: 0.04, hold: 0.55, r: 0.85, peak: 0.75, breath: 0.2 });
    },
  },
  cheer: {
    dur: 3.1,
    render(k) {
      crowd(k, { vowel: (i) => (['a', 'e', 'i'] as const)[i % 3], contour: [[0, 1.2], [0.3, 1.55], [0.8, 1.3], [1.6, 1.1]], a: 0.05, hold: 0.9, r: 1.0, peak: 0.6, breath: 0.25 });
      claps(k, 0.05, 2.9, (x) => 70 * (1 - x) ** 1.3 + 4, 0.35);
    },
  },
  laugh: {
    dur: 1.6,
    render(k) {
      crowd(k, { vowel: (i) => (i % 3 === 0 ? 'a' : 'o'), contour: [[0, 1.3], [1.4, 0.95]], a: 0.03, hold: 1.0, r: 0.4, peak: 0.6, pulse: [6.5, 1], breath: 0.5 });
    },
  },
  gasp: {
    dur: 0.65,
    render(k) {
      // Breath in: aspiration noise through an "ah" that closes toward "oh".
      const bus = k.gain(1 / Math.sqrt(VOICES));
      for (let i = 0; i < VOICES; i++) {
        const at = k.range(0, 0.06);
        const n = k.noise('white', at, at + 0.6);
        const b1 = k.bp(k.range(700, 900), 4);
        k.pitchEnv(b1.frequency, b1.frequency.value, 480, 0.4, 'exp', at);
        const b2 = k.bp(k.range(1100, 1300), 5);
        const sum = k.gain(1);
        n.connect(b1).connect(sum);
        n.connect(b2).connect(sum);
        k.chain(sum, k.env({ a: 0.06, d: 0.1, s: 0.6, hold: 0.15, r: 0.2, peak: k.range(0.5, 1), at }), bus);
      }
      k.toOut(bus, k.hp(300), k.gain(1.4));
    },
  },
  /**
   * One unison clap of the whole stands (the clap-along on 2 and 4 when the round gets tense):
   * ~30 hands inside ~30 ms, a few stragglers, and the low body of many palms at once.
   */
  clap: {
    dur: 0.32,
    render(k) {
      claps(k, 0.002, 0.035, () => 900, 0.6);
      claps(k, 0.03, 0.05, () => 140, 0.22);
      const body = k.noise('pink', 0, 0.12);
      k.toOut(body, k.bp(650, 1.1), k.env({ a: 0.002, d: 0.06, s: 0, r: 0, peak: 0.5, at: 0.004 }));
    },
  },
  /** GAME! roar: cheer and a bigger, longer applause. */
  roar: {
    dur: 4,
    render(k) {
      crowd(k, { vowel: (i) => (['a', 'o', 'e'] as const)[i % 3], contour: [[0, 1.1], [0.4, 1.6], [1.5, 1.35], [3, 1.1]], a: 0.08, hold: 1.8, r: 1.4, peak: 0.75, breath: 0.3 });
      claps(k, 0.1, 3.8, (x) => 110 * (1 - x) ** 1.1 + 6, 0.4);
    },
  },
} satisfies Record<string, Recipe>;
export type CrowdSound = keyof typeof CROWD_SOUNDS;
